import React, { useState, useEffect } from "react";
import { IconButton, Tooltip } from "@mui/material";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { ThemeProvider } from "@mui/system";
import * as DefaultRoutes from "@Routes/DefaultRoutes";
import GenericExitRequestListing from "../../../GenericExitRequestListing/GenericExitRequestListing";
import dayjs from "dayjs";
import usePaginate from "@Hooks/usePaginate";
import useSendGetRequest from "@Hooks/useSendGetRequest";
import { REGIONS_URL, COUNTRIES_URL } from "@API/APIRoutes";
import {
  stringCustomOperators,
  numberCustomOperators,
  selectCustomOperators,
} from "@Utils/Validations/HelperFunctions/CustomFilters";
import {TagsContainer, TagItem} from "../../../Admin/ListingItems/ListingItems";

function GenericCanceledExitRequests({ API, exportApi, title }) {
  const { loading: pageLoading, getPage } = usePaginate();
  const { getData: getRegions } = useSendGetRequest();
  const { getData: getCountries } = useSendGetRequest();
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [regions, setRegions] = useState([]);
  const [countries, setCountries] = useState([]);
  const errorMessage = "Failed to fetch Requests !";
  const isCancelled = true;

  // Fetch regions and countries on mount for filter dropdowns
  useEffect(() => {
    getRegions(REGIONS_URL).then((data) => {
      if (data) setRegions(data);
    });
    getCountries(COUNTRIES_URL).then((data) => {
      if (data) setCountries(data);
    });
  }, []);

  // Format date to readable format
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return dayjs(dateStr).format("MM/DD/YYYY");
  };

  const columns = [
    {
      field: "id",
      headerName: "Request ID",
      flex: 1.0,
      minWidth: 80,
      type: "number",
      align: 'left',
      filterOperators: numberCustomOperators,
    },
    {
      field: "ingredient",
      headerName: "Ingredient",
      flex: 1.2,
      type: "string",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div>
          <div>{params.row.ingredient.name}</div>
          <div style={{ fontSize: "0.75rem", color: "gray" }}>
            CAS: {params.row.ingredient.casNumber}
          </div>
        </div>
      ),
      filterOperators: stringCustomOperators,
    },
    {
      field: "ingredientName",
      headerName: "Ingredient Name",
      flex: 1.2,
      type: "string",
      valueGetter: (params) => params.row.ingredient.name,
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    {
      field: "ingredientCasNumber",
      headerName: "CAS Number",
      flex: 1,
      minWidth: 110,
      type: "string",
      valueGetter: (params) => params.row.ingredient.casNumber,
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    {
      field: "userEmail",
      headerName: "Created By",
      flex: 1.5,
      minWidth: 160,
      renderCell: (params) => (
        <div>
          <div>{params.row.user.email}</div>
        </div>
      ),
      type: "string",
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    {
      field: "restrictionType",
      headerName: "Restriction",
      flex: 1.3,
      minWidth: 130,
      type: "string",
      filterOperators: stringCustomOperators,
    },
     {
      field: "regionId",
      headerName: "Region",
      flex: 1.2,
      minWidth: 130,
      type: "singleSelect",
      valueOptions: regions.map((r) => ({ value: String(r.id), label: r.name })),
      filterOperators: selectCustomOperators,
      renderCell: (params) => {
        const regions = params.row.regions;
        let items = [];
        if (Array.isArray(regions) && regions.length > 0) {
          items = [...new Set(regions.filter(Boolean))];
        } else {
          const rawCountries = params.row["countries"] || [];
          items = [...new Set(rawCountries.map(i => i?.regionName).filter(Boolean))];
        }

        return (
          <TagsContainer>
            {items.map((item, index) => (
              <TagItem key={index}>{item}</TagItem>
            ))}
          </TagsContainer>
        );
      },
      sortable: false,
    },
    {
      field: "countryId",
      headerName: "Country",
      flex: 1.2,
      minWidth: 130,
      type: "singleSelect",
      valueOptions: countries.map((c) => ({ value: String(c.id), label: c.name })),
      filterOperators: selectCustomOperators,
      valueGetter: (params) => {
        const countriesArr = params.row.countries;
        if (!countriesArr || countriesArr.length === 0) return "";
        return countriesArr.map((c) => c.name).join(", ");
      },
      sortable: false,
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 1.2,
      minWidth: 120,
      valueGetter: (params) =>
        params.row.createdAt ? new Date(params.row.createdAt) : null,
      valueFormatter: (params) => formatDate(params.value),
      type: "date",
    },
    {
      field: "primaryDate",
      headerName: "Primary Date",
      flex: 1.2,
      minWidth: 120,
      valueGetter: (params) =>
        params.row.primaryDate ? new Date(params.row.primaryDate) : null,
      valueFormatter: (params) => formatDate(params.value),
      type: "date",
    },
    {
      field: "secondaryDate",
      headerName: "Secondary Date",
      flex: 1.2,
      minWidth: 120,
      valueGetter: (params) =>
        params.row.secondaryDate ? new Date(params.row.secondaryDate) : null,
      valueFormatter: (params) => formatDate(params.value),
      type: "date",
    },
    {
      field: "CancelledAt",
      headerName: "Cancelled On",
      flex: 1.2,
      minWidth: 120,
      valueGetter: (params) =>
        params.row.cancelledAt ? new Date(params.row.cancelledAt) : null,
      valueFormatter: (params) => formatDate(params.value),
      type: "date",
    },
    {
      field: "numberOfRemainingResponses",
      headerName: "Impact Assessments Remaining",
      flex: 1,
      minWidth: 100,
      type: "number",
      filterOperators: numberCustomOperators,
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      width: 120,
      minWidth: 120,
      maxWidth: 120,
      disableReorder: true,
    },
  ];
  const initialColumnVisibilityModel = {
    numberOfRemainingResponses: false,
    createdAt: false,
    ingredientCasNumber: false,
    ingredientName: false,
    countries: false,
    regionId: false,
    countryId: false,
    secondaryDate: false,
    ulInternalDate: false,
  };
  const renderActions = (params, navigate, theme) => (
    <ThemeProvider theme={theme}>
      <Tooltip title="Preview Request">
        <IconButton
          color="info"
          onClick={() =>
            navigate(
              DefaultRoutes.canceledReqInfo.replace(":reqId", params.row.id)
            )
          }
          size="small"
        >
          <LightbulbOutlinedIcon />
        </IconButton>
      </Tooltip>
    </ThemeProvider>
  );

  const fetchPage = (queryOptions, filterModel) => {
    const filterItems = (filterModel?.items || []).map((item) => {
      const cleaned = { ...item };
      if (cleaned.value && typeof cleaned.value === "string") {
        cleaned.value = cleaned.value.trim();
        if (cleaned.field === "state") {
          cleaned.value = cleaned.value.replace(/\s+/g, "_");
        }
      }
      return cleaned;
    }).filter((item) => item.field && item.operator);

    getPage(
      `${API}` + `/` + `${isCancelled}`,
      errorMessage,
      queryOptions,
      filterItems
    ).then((data) => {
      if (data) {
        setRows(data.content);
        setRowCount(data.totalElements);
      }
    });
  };

  return (
    <GenericExitRequestListing
      title={title}
      PageIcon={<CancelRoundedIcon color="white" fontSize="large" />}
      iconColor="error.main"
      columns={columns}
      renderActions={renderActions}
      errorMessage="Failed to Fetch Canceled Exit Requests Trying Again!"
      buttons={[]}
      fetchPage={fetchPage}
      pageLoading={pageLoading}
      rowCount={rowCount}
      rows={rows}
      initialColumnVisibilityModel={initialColumnVisibilityModel}
      exportApi={exportApi ? `${exportApi}/${isCancelled}` : null}
    />
  );
}

export default GenericCanceledExitRequests;
